import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { requiredPermissionsForPath } from '@/lib/corePermissions';

// ─────────────────────────────────────────────────────────────
// Garde d'accès des routes du dashboard.
// Si la page courante requiert une permission que l'utilisateur
// connecté ne possède pas, on affiche un écran « accès refusé »
// au lieu de la page. Les chemins sans règle restent autorisés.
// ─────────────────────────────────────────────────────────────

export default function PermissionGuard({ children }: { children: ReactNode }) {
  const { ready, has } = usePermissions();
  const { pathname } = useLocation();

  if (!ready) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  const required = requiredPermissionsForPath(pathname);

  if (!has(required)) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-4">
          <i className="ri-lock-2-line text-3xl text-accent-700"></i>
        </div>
        <h2 className="text-lg font-bold font-heading text-foreground-950">Accès non autorisé</h2>
        <p className="text-sm text-foreground-500 mt-2 max-w-md">
          Votre rôle ne vous donne pas accès à cette section. Demandez au propriétaire de votre
          entreprise de vous accorder la permission correspondante.
        </p>
        <Link
          to="/dashboard"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Retour au tableau de bord
        </Link>
      </div>
    );
  }

  return <>{children}</>;
}